import { LandingNavbar } from "./Navbar";
import { BideshaiBrand } from "./BideshaiBrand";
import { useLanguage } from "../i18n/LanguageContext";

// ─── LOCAL ASSETS ─────────────────────────────────────────────────────────────
const imgHero = "/landing/hero-background.jpg";
const imgStep01 = "/landing/step-01.png";
const imgStep02 = "/landing/step-02.png";
const imgStep03 = "/landing/step-03.png";
const imgIconD7  = "/icons/icon-d7.svg";
const imgIconD8  = "/icons/icon-d8.svg";
const imgIconD2  = "/icons/icon-d2.svg";
const imgIconD3  = "/icons/icon-d3.svg";
const imgIconD1  = "/icons/icon-d1.svg";
const imgIconD4  = "/icons/icon-d4.svg";
const imgIconD6  = "/icons/icon-d6.svg";
const imgIconARI = "/icons/icon-ari.svg";

// ─── DATA ─────────────────────────────────────────────────────────────────────

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

function GradientBadge({ children, fontSize = 20 }) {
  return (
    <div style={{
      background: "linear-gradient(180deg, #f4f9f8 0%, #cbe9e3 100%)",
      borderRadius: 24,
      padding: "4px 12px",
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
    }}>
      <span style={{
        background: "linear-gradient(180deg, #1abc9d 0%, #3e7179 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        fontWeight: 600,
        fontSize,
        letterSpacing: "3px",
        textTransform: "uppercase",
        lineHeight: "28px",
        whiteSpace: "nowrap",
      }}>
        {children}
      </span>
    </div>
  );
}

function GradientButton({ children, onClick, className = "lp-section-btn" }) {
  return (
    <button
      onClick={onClick}
      className={className}
      style={{
        background: "linear-gradient(180deg, #1abc9d 0%, #3e7179 100%)",
        color: "#ffffff",
        border: "none",
        borderRadius: 100,
        padding: "12px 32px",
        height: 48,
        fontSize: 16,
        fontWeight: 400,
        letterSpacing: "0.64px",
        cursor: "pointer",
        fontFamily: "'Outfit', sans-serif",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      {children}
    </button>
  );
}

function ContactEmailIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4.5" y="6.5" width="15" height="11" rx="3" stroke="#1ABC9D" strokeWidth="1.5" />
      <path d="M7.5 9.5 12 12.75 16.5 9.5" stroke="#1ABC9D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ContactWhatsAppIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5.25a6.75 6.75 0 0 0-5.77 10.26L5.25 18.75l3.37-.88A6.75 6.75 0 1 0 12 5.25Z" stroke="#1ABC9D" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M9.55 10.1c.19-.42.4-.43.58-.44h.5c.13 0 .32.05.4.24l.5 1.2c.07.16.06.34-.04.48l-.34.45c-.08.1-.1.24-.04.36.16.34.43.7.76 1.02.34.34.72.63 1.08.8.12.06.27.04.37-.05l.42-.37c.14-.12.34-.15.5-.07l1.14.54c.18.08.23.28.22.41v.48c-.01.18-.02.4-.42.57-.41.18-1.37.22-2.67-.37-1.08-.49-2.27-1.57-2.92-2.73-.64-1.15-.56-1.97-.31-2.48Z" fill="#1ABC9D" />
    </svg>
  );
}

function VisaCard({ visa, onClick }) {
  return (
    <div
      className="lp-visa-card"
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      style={{
      background: visa.cardBg,
      borderRadius: 24,
      padding: 24,
      display: "flex",
      flexDirection: "column",
      gap: 24,
      cursor: "pointer",
    }}>
      {/* Icon row */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{
          background: visa.iconBg,
          borderRadius: 23.7,
          width: 80, height: 80,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <img src={visa.icon} alt={visa.name} style={{ width: 32, height: 32, display: "block" }} />
        </div>
        <GradientBadge>{visa.code}</GradientBadge>
      </div>

      {/* Text */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ margin: 0, fontSize: 22, fontWeight: 400, lineHeight: "30px", color: "#12203e" }}>
          {visa.name}
        </p>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 300, lineHeight: "24px", color: "#4a5565" }}>
          {visa.tagline}
        </p>
      </div>

      <span style={{ marginTop: "auto", fontSize: 12, fontWeight: 500, color: "#1ABC9C", lineHeight: 1.4 }}>
        Learn more →
      </span>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function LandingPage({ onStart, onGuide }) {
  const { t } = useLanguage();
  const STEPS = [
    {
      num: t("step1_badge"),
      title: t("step1_title"),
      body: t("step1_desc"),
      imgBg: "#f1f2ff",
      image: imgStep01,
      imageStyle: { width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" },
      cta: "Start →",
    },
    {
      num: t("step2_badge"),
      title: t("step2_title"),
      body: t("step2_desc"),
      imgBg: "#ffffff",
      image: imgStep02,
      imageStyle: { width: "86.7%", height: "94.8%", objectFit: "contain", objectPosition: "center center" },
      cta: "Start →",
    },
    {
      num: t("step3_badge"),
      title: t("step3_title"),
      body: t("step3_desc"),
      imgBg: "#f1f2ff",
      image: imgStep03,
      imageStyle: { width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" },
      cta: "Start →",
    },
  ];
  const VISA_CARDS = [
    { code: "D7", sectionId: "d7", name: t("visa_d7_name"), tagline: t("visa_d7_tagline"), icon: imgIconD7, iconBg: "#eef0fe", cardBg: "#ffffff" },
    { code: "D8", sectionId: "d8", name: t("visa_d8_name"), tagline: t("visa_d8_tagline"), icon: imgIconD8, iconBg: "#d7ff89", cardBg: "#ffffff" },
    { code: "D2", sectionId: "d2", name: t("visa_d2_name"), tagline: t("visa_d2_tagline"), icon: imgIconD2, iconBg: "#feebea", cardBg: "#ffffff" },
    { code: "D3", sectionId: "d3", name: t("visa_d3_name"), tagline: t("visa_d3_tagline"), icon: imgIconD3, iconBg: "#fff9c4", cardBg: "#ffffff" },
    { code: "D1", sectionId: "d1", name: t("visa_d1_name"), tagline: t("visa_d1_tagline"), icon: imgIconD1, iconBg: "#ffe6c4", cardBg: "#ffffff" },
    { code: "D4", sectionId: "d4", name: t("visa_d4_name"), tagline: t("visa_d4_tagline"), icon: imgIconD4, iconBg: "#eaf7fe", cardBg: "#ffffff" },
    { code: "D6", sectionId: "d6", name: t("visa_d6_name"), tagline: t("visa_d6_tagline"), icon: imgIconD6, iconBg: "#feeafb", cardBg: "#ffffff" },
    { code: "ARI", sectionId: "ari", name: t("visa_ari_name"), tagline: t("visa_ari_tagline"), icon: imgIconARI, iconBg: "#cfffd8", cardBg: "#ffffff" },
  ];

  return (
    <div className="lp-page" style={{ fontFamily: "'Outfit', sans-serif", background: "#ffffff", minWidth: 320 }}>

      <LandingNavbar onStart={onStart} onGuide={onGuide} />

      {/* ── HERO ── */}
      <section className="lp-hero-section" style={{ padding: "16px 60px 0", background: "#ffffff" }}>
          <div className="lp-hero-media" style={{
            position: "relative",
            height: 600,
            borderRadius: 24,
            overflow: "hidden",
          }}>
          <img
            src={imgHero}
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />

          {/* Teal gradient overlay (matches Figma) */}
          <div className="lp-hero-overlay" style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(179.4deg, rgba(29,172,195,0.36) 1.06%, rgba(14,82,93,0.72) 56.22%)",
            borderRadius: 24,
          }} />

          {/* Centred content */}
          <div className="lp-hero-inner" style={{
            position: "absolute", inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 36,
            padding: "0 48px",
            textAlign: "center",
          }}>
            {/* Badge */}
            <div className="lp-hero-badge-wrap" style={{
              background: "rgba(14,82,93,0.8)",
              borderRadius: 24,
              padding: "2px 16px",
            }}>
              <span className="lp-hero-badge-text" style={{
                color: "#ffffff",
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "1px",
                textTransform: "uppercase",
                lineHeight: "28px",
              }}>{t("hero_badge")}</span>
            </div>

            {/* Headline */}
            <h1 className="lp-hero-title" style={{
              margin: 0,
              fontSize: "clamp(36px, 5vw, 68px)",
              fontWeight: 600,
              color: "#ffffff",
              lineHeight: 1.05,
              letterSpacing: "2px",
              textShadow: "0px 4px 4px rgba(0,0,0,0.15)",
            }}>
              {t("hero_headline")}
            </h1>

            {/* Subtext */}
            <p className="lp-hero-sub" style={{
              margin: 0,
              fontSize: "clamp(16px, 2vw, 22px)",
              fontWeight: 300,
              color: "#ffffff",
              lineHeight: "32px",
              maxWidth: 640,
            }}>
              {t("hero_sub")}
            </p>

            {/* CTA */}
            <button
              onClick={onStart}
              className="lp-hero-cta"
              style={{
                background: "#ffffff",
                color: "#0e525d",
                border: "none",
                borderRadius: 100,
                padding: "16px 36px",
                fontSize: "clamp(15px, 1.5vw, 19px)",
                fontWeight: 500,
                letterSpacing: "0.8px",
                cursor: "pointer",
                fontFamily: "'Outfit', sans-serif",
                boxShadow: "0px 8px 24px 0px rgba(255,255,255,0.4)",
                lineHeight: "28px",
                whiteSpace: "nowrap",
              }}
            >
              {t("hero_cta")}
            </button>

            {/* Trust signal */}
            <p className="lp-hero-trust" style={{
              margin: 0,
              fontSize: 15,
              fontWeight: 300,
              color: "rgba(255,255,255,0.85)",
              lineHeight: "28px",
            }}>
              {t("hero_trust")}
            </p>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="lp-section-how" style={{ padding: "80px 60px", background: "#ffffff" }}>
        <div className="lp-how-inner" style={{ maxWidth: 1320, margin: "0 auto", display: "flex", flexDirection: "column", gap: 48, alignItems: "center" }}>
          {/* Section heading */}
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12, textAlign: "center" }}>
            <p style={{
              margin: 0, fontSize: 16, fontWeight: 300,
              color: "#0e525d", letterSpacing: "1.44px",
              textTransform: "uppercase", lineHeight: 1.4,
            }}>{t("how_label")}</p>
            <h2 className="lp-how-title" style={{
              margin: 0,
              fontSize: "clamp(28px, 3.5vw, 44px)",
              fontWeight: 400, color: "#140810",
              lineHeight: 1.2, letterSpacing: "2px",
            }}>{t("how_title")}</h2>
          </div>

          {/* Step cards — flex-wrap so they collapse on tablet */}
          <div className="lp-steps-row" style={{ display: "flex", gap: 20, width: "100%", alignItems: "stretch", flexWrap: "wrap" }}>
            {STEPS.map((step) => (
              <div
                key={step.num}
                className="lp-step-card"
                onClick={onStart}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onStart?.();
                  }
                }}
                role="button"
                tabIndex={0}
                style={{
                flex: "1 1 260px",
                minWidth: 0,
                minHeight: 480,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 28,
                cursor: "pointer",
              }}>
                {/* Text */}
                <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                  <div className="lp-step-pill" style={{
                    padding: "12px 22px",
                    display: "inline-flex",
                    alignSelf: "flex-start",
                  }}>
                    <span style={{ fontSize: 16, fontWeight: 300, lineHeight: 1 }}>
                      {step.num}
                    </span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <p style={{ margin: 0, fontSize: 22, fontWeight: 300, color: "#252725", lineHeight: 1.35 }}>
                      {step.title}
                    </p>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 300, color: "#252725", lineHeight: 1.5 }}>
                      {step.body}
                    </p>
                  </div>
                </div>

                {/* Step illustration */}
                <div style={{
                  background: step.imgBg,
                  borderRadius: 24,
                  height: 308,
                  flexShrink: 0,
                  marginTop: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  position: "relative",
                }}>
                  <img
                    src={step.image}
                    alt={step.title}
                    loading="lazy"
                    style={step.imageStyle}
                  />
                </div>

                <span style={{ marginTop: "auto", fontSize: 13, fontWeight: 500, color: "#1ABC9C", lineHeight: 1.4 }}>
                  {step.cta}
                </span>
              </div>
            ))}
          </div>

          <GradientButton onClick={onStart}>{t("start_checker")}</GradientButton>
        </div>
      </section>

      {/* ── VISA CATEGORIES ── */}
      <section id="visa-categories" className="lp-section-visas" style={{ background: "#f3f9f8", padding: "80px 60px" }}>
        <div className="lp-visas-inner" style={{ maxWidth: 1248, margin: "0 auto", display: "flex", flexDirection: "column", gap: 56, alignItems: "center" }}>
          {/* Heading */}
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 20, alignItems: "center", textAlign: "center" }}>
            <p style={{
              margin: 0, fontSize: 16, fontWeight: 300, color: "#0e525d",
              letterSpacing: "1.44px", textTransform: "uppercase", lineHeight: 1.4,
            }}>{t("visas_label")}</p>
            <h2 style={{
              margin: 0,
              fontSize: "clamp(28px, 3.5vw, 44px)",
              fontWeight: 400, color: "#140810",
              lineHeight: 1.2, letterSpacing: "2px",
            }}>{t("visas_title")}</h2>
            <p style={{
              margin: 0, fontSize: "clamp(15px, 1.5vw, 20px)", fontWeight: 300,
              color: "#140810", lineHeight: "28px", letterSpacing: "0.4px",
              maxWidth: 600,
            }}>
              {t("visas_sub")}
            </p>
          </div>

          {/* Responsive grid — auto-fills 4 per row on desktop, collapses on smaller screens */}
          <div className="lp-visa-grid" style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 20,
          }}>
            {VISA_CARDS.map((v) => (
              <VisaCard
                key={v.code}
                visa={v}
                onClick={() => onGuide?.(v.sectionId)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="lp-section-contact" style={{
        background: "#ffffff",
        padding: "80px 60px",
        textAlign: "center",
      }}>
        <div className="lp-contact-inner" style={{ maxWidth: 560, margin: "0 auto", display: "flex", flexDirection: "column", gap: 24, alignItems: "center" }}>
          <p style={{
            margin: 0, fontSize: 13, fontWeight: 600, letterSpacing: "1.2px",
            color: "#0e525d", textTransform: "uppercase",
          }}>{t("contact_label")}</p>
          <h2 style={{
            margin: 0,
            fontSize: "clamp(24px, 3.5vw, 36px)",
            fontWeight: 400, color: "#140810",
            lineHeight: 1.25, letterSpacing: "1px",
          }}>{t("contact_title")}</h2>
          <p style={{
            margin: 0, fontSize: 16, fontWeight: 300, color: "#4a5565",
            lineHeight: 1.7,
          }}>
            {t("contact_sub")}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <a
              href="mailto:uxmoin@gmail.com"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: "#F0F9F7",
                border: "1px solid #1ABC9D",
                borderRadius: 100,
                padding: "12px 24px",
                fontSize: 16,
                fontWeight: 400,
                color: "#1ABC9D",
                textDecoration: "none",
                fontFamily: "'Outfit', sans-serif",
                lineHeight: 1.32,
                letterSpacing: "0.64px",
                transition: "opacity 0.15s ease",
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
            >
              <ContactEmailIcon />
              <span
                style={{
                  background: "linear-gradient(180deg, #1ABC9D 0%, #3E7179 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Email
              </span>
            </a>
            <a
              href="https://wa.me/351920277885"
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: "#F0F9F7",
                border: "1px solid #1ABC9D",
                borderRadius: 100,
                padding: "12px 24px",
                fontSize: 16,
                fontWeight: 400,
                color: "#1ABC9D",
                textDecoration: "none",
                fontFamily: "'Outfit', sans-serif",
                transition: "opacity 0.15s ease",
                lineHeight: 1.32,
                letterSpacing: "0.64px",
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
            >
              <ContactWhatsAppIcon />
              <span
                style={{
                  background: "linear-gradient(180deg, #1ABC9D 0%, #3E7179 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                WhatsApp
              </span>
            </a>
            <GradientButton onClick={onStart}>{t("hero_cta")}</GradientButton>
          </div>
        </div>
      </section>

      {/* ── DISCLAIMER ── */}
      <section className="lp-section-disclaimer" style={{
        background: "radial-gradient(ellipse at center, #357968 0%, #216663 50%, #0e525d 100%)",
        padding: "80px clamp(24px, 12vw, 261px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 28,
        boxSizing: "border-box",
        textAlign: "center",
      }}>
        <BideshaiBrand height={44} color="#FFFFFF" />
        <p style={{
          margin: 0,
          fontSize: "clamp(14px, 1.2vw, 18px)",
          fontWeight: 300,
          color: "rgba(255,255,255,0.8)",
          lineHeight: "28px",
          letterSpacing: "0.36px",
          maxWidth: 860,
        }}>
          {t("footer_legal")}
        </p>
      </section>

      {/* ── FOOTER ── */}
      <footer className="lp-site-footer" style={{
        background: "#ffffff",
        padding: "16px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderTop: "1px solid #f0f0f0",
      }}>
        <p className="lp-site-footer-text" style={{
          margin: 0,
          fontSize: 14,
          fontWeight: 300,
          color: "#738c8b",
          lineHeight: "28px",
          letterSpacing: "0.28px",
          textAlign: "center",
        }}>
          {t("footer_built")}
        </p>
      </footer>

    </div>
  );
}
